import { useEffect, useState, type MouseEvent } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import '../LandingPage.css';

interface HeaderProps {
  /** When true, the header floats transparently over a dark hero. */
  overlay?: boolean;
}

function Header({ overlay = true }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  const handleHomeClick = (event: MouseEvent<HTMLAnchorElement>) => {
    closeMenu();
    // Already on the landing page: React Router won't re-navigate to "/",
    // so scroll back to the top ourselves.
    if (location.pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContactClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    closeMenu();
    if (location.pathname === '/') {
      document
        .getElementById('contact')
        ?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Coming from another route: navigate home with the hash so the
      // landing page can scroll to the contact section once it mounts.
      navigate('/#contact');
    }
  };

  const getNavLinkClass = ({ isActive }: { isActive: boolean }): string =>
    `nav-link ${isActive ? 'active' : ''}`;

  const solid = !overlay || scrolled;

  return (
    <header className={`header ${solid ? 'header--scrolled' : ''}`}>
      <div className="header-content">
        <NavLink to="/" className="logo" onClick={closeMenu}>
          EF<span>Soft</span>
        </NavLink>

        <button
          className={`burger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
          <NavLink to="/" className={getNavLinkClass} onClick={handleHomeClick} end>
            Home
          </NavLink>
          <NavLink to="/products" className={getNavLinkClass} onClick={closeMenu}>
            Products
          </NavLink>
          <a href="/#contact" className="nav-cta" onClick={handleContactClick}>
            Get in touch
          </a>
        </nav>
      </div>
    </header>
  );
}

export default Header;
